import React from 'react';
import { runsOf } from '@versesverse/core';
import type { Mark, NameSpan } from '@versesverse/core';

/*
 * VERSE TEXT, WITH ITS NAMES AS DOORS.
 *
 * Reading a verse should not be a dead end: every name the map knows, sitting in the text, is a way
 * to that name's own verses. Given the character spans (from `nameSpans`, computed once when the
 * passage is built), this splits the text into plain runs and buttons — click one and the map
 * travels to that name and opens its panel.
 *
 * A button, not an anchor: it moves the camera and swaps a panel inside this app, it does not
 * navigate anywhere, so the control that says "activatable, stays here" is the honest one — and it
 * is reachable by keyboard and named for a screen reader by the word it wraps. With no spans, or no
 * handler to receive the jump, it is simply the text.
 */
export interface LinkedTextProps {
  text: string;
  links: NameSpan[];
  onJump?: (id: string) => void;
  /**
   * WHERE SOMEBODY IS POINTING — §11, and the reason this file no longer cuts the text itself.
   *
   * Marks and names overlap: a leader points at *"Abraham rose"* and half of it is a door to that
   * name. Choosing one of the two would lose the other, so the cutting moved to `runsOf` in core,
   * where it is a pure function with the rule that matters written on it — **the runs concatenated
   * are the verse** — and where the phone will find it when it grows a reader.
   *
   * Offsets belong to the EDITION they were measured in (§11), so a mark whose edition is not the
   * one being drawn is not applied narrowly: the caller passes only what belongs here.
   */
  marks?: readonly Mark[];
}

export function LinkedText({ text, links, onJump, marks = [] }: LinkedTextProps): React.JSX.Element {
  /* Nothing to draw around: the text, as it always was. A verse with names but no handler is still
     just words, and a verse with neither names nor marks never needed cutting. */
  if ((links.length === 0 || !onJump) && marks.length === 0) return <>{text}</>;

  const runs = runsOf(text, onJump ? links : [], marks);

  return (
    <>
      {runs.map((run, i) => {
        /* A name inside a mark, rather than a mark inside a name: `<mark>` is the semantic wrapper
           for "somebody is pointing here", and a screen reader should hear that before the control
           it contains. */
        const inner = run.link !== undefined && onJump
          ? (
            <button type="button" className="verse-jump" onClick={() => onJump(run.link ?? '')}>
              {run.text}
            </button>
          )
          : run.text;
        if (!run.marked) return <React.Fragment key={i}>{inner}</React.Fragment>;
        return <mark key={i} className="verse-marked">{inner}</mark>;
      })}
    </>
  );
}
